import React, { useMemo, useState } from 'react';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useNavigate } from 'react-router-dom';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import ReceiptsModal from '../components/invoices/ReceiptsModal';
import { CrmBadge, CrmButton, CrmTooltip } from '../components/ui';
import { CrmEntityListPage } from '../components/listing';
import { formatDate } from '../utils/dateFormatter';
import { formatCurrency } from '../utils/currencyUtils';
import { getInvoices } from '../services/invoiceService';
import { useSettings } from '../context/SettingsContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const sourceLabel = (row) => {
  if (row?.source_label) return row.source_label;
  const source = String(row?.source_type || '').toUpperCase();
  if (source === 'FRONTEND_ORDER' || source === 'WEBSITE_ORDER') return 'Website';
  if (source === 'WORK_ORDER') return 'Catering';
  if (source === 'QUOTATION') return 'Quotation';
  return source ? source.replace(/_/g, ' ') : 'Manual';
};

const parseSnapshot = (snapshot) => {
  if (!snapshot) return {};
  if (typeof snapshot === 'object') return snapshot;
  try { return JSON.parse(snapshot); } catch { return {}; }
};

const customerName = (row) => {
  const billing = parseSnapshot(row?.billing_snapshot);
  return row?.customer_name || `${row?.first_name || ''} ${row?.last_name || ''}`.trim() || billing.name || row?.company_name || '—';
};

const totalPaid = (invoice) => (Array.isArray(invoice?.payments)
  ? invoice.payments.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0)
  : Number(invoice?.grand_total || 0));

const latestPaymentDate = (invoice) => {
  const values = (Array.isArray(invoice?.payments) ? invoice.payments : [])
    .map((payment) => payment?.paymentDate).filter(Boolean);
  return values.sort((left, right) => new Date(right) - new Date(left))[0] || null;
};

function PaymentHistory() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptsModalOpen, setReceiptsModalOpen] = useState(false);
  const [receiptsModalInvoice, setReceiptsModalInvoice] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const loadRows = async ({ isAutoRefresh = false } = {}) => {
    if (!isAutoRefresh) setLoading(true);
    try {
      const response = await getInvoices();
      setRows((Array.isArray(response) ? response : []).filter((invoice) => String(invoice?.status || '').toLowerCase() === 'paid'));
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Failed to load payment history', severity: 'error' });
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  };

  useAutoRefresh(loadRows, { intervalMs: 15000 });

  const sourceOptions = useMemo(() => [...new Set(rows.map(sourceLabel).filter(Boolean))]
    .map((value) => ({ value, label: value })), [rows]);

  const columns = useMemo(() => [
    {
      id: 'invoice_number', label: 'Invoice #', width: 160, minWidth: 130, maxWidth: 240,
      hideable: false, defaultPinned: true, pinnable: true, stopRowClick: true,
      renderCell: (row) => <button type="button" className="crm-table-primary-link" onClick={() => navigate(`/invoices/${row.id}`)}>{row.invoice_number || '—'}</button>,
    },
    { id: 'customer', label: 'Customer', width: 220, minWidth: 170, maxWidth: 380, accessor: customerName },
    { id: 'source', label: 'Source', width: 130, minWidth: 110, maxWidth: 200, filterable: true, accessor: sourceLabel },
    { id: 'paid_date', label: 'Paid date', width: 150, minWidth: 130, maxWidth: 220, type: 'date', filterable: true, accessor: latestPaymentDate, renderCell: (row) => formatDate(latestPaymentDate(row)) || '—' },
    { id: 'grand_total', label: 'Invoice total', width: 160, minWidth: 140, maxWidth: 230, type: 'currency', accessor: (row) => Number(row.grand_total || 0), renderCell: (row) => formatCurrency(row.grand_total, settings?.currency_code) },
    { id: 'total_paid', label: 'Total paid', width: 160, minWidth: 140, maxWidth: 230, type: 'currency', accessor: totalPaid, renderCell: (row) => formatCurrency(totalPaid(row), settings?.currency_code) },
    { id: 'status', label: 'Status', width: 120, minWidth: 100, maxWidth: 170, renderCell: () => <CrmBadge tone="success">Paid</CrmBadge> },
    {
      id: 'row_actions', label: 'Actions', width: 92, minWidth: 92, maxWidth: 120,
      hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (row) => (
        <CrmTooltip content="View receipts">
          <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" disabled={!row.payments?.length} onClick={() => { setReceiptsModalInvoice(row); setReceiptsModalOpen(true); }} aria-label="View receipts"><ReceiptLongOutlinedIcon /></CrmButton>
        </CrmTooltip>
      ),
    },
  ], [navigate, settings?.currency_code]);

  const filters = useMemo(() => [
    { id: 'paid_date', columnId: 'paid_date', label: 'Paid date', type: 'date-range', quick: true, getValue: latestPaymentDate },
    { id: 'source', columnId: 'source', label: 'Source', type: 'select', options: sourceOptions, quick: true, getValue: sourceLabel },
  ], [sourceOptions]);

  const summary = useMemo(() => [
    { label: 'Paid invoices', value: rows.length },
    { label: 'Total paid', value: formatCurrency(rows.reduce((sum, row) => sum + totalPaid(row), 0), settings?.currency_code) },
  ], [rows, settings?.currency_code]);

  return (
    <div className="contacts-page">
      <CrmEntityListPage
        moduleKey="payment-history"
        title="Payment History"
        itemLabel="payment"
        items={rows}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'paid_date', direction: 'desc' }}
        loading={loading}
        onRefresh={loadRows}
        onSettings={() => navigate('/payment-reminders/settings')}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        summaryItems={summary}
        headerActions={<CrmButton size="small" onClick={() => navigate('/payments')}>Pending payments</CrmButton>}
      />
      <ReceiptsModal open={receiptsModalOpen} invoice={receiptsModalInvoice} onClose={() => { setReceiptsModalOpen(false); setReceiptsModalInvoice(null); }} onError={(message) => setNotification({ open: true, message, severity: 'error' })} />
      <NotificationSnackbar {...notification} onClose={() => setNotification((previous) => ({ ...previous, open: false }))} />
    </div>
  );
}

export default PaymentHistory;
